import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { getAllAgentsAdmin, createAgent, updateAgent, deleteAgent } from '@/lib/agents-api';
import { IconByName, HABIT_ICON_OPTIONS } from '@/lib/category-icons';
import type { CoachingAgentAdmin } from '@/types';
import { Button } from '@lifecoach/ui';

export function AgentManagement() {
  const [agents, setAgents] = useState<CoachingAgentAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CoachingAgentAdmin>>({});
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadAgents();
  }, []);

  async function loadAgents() {
    try {
      const data = await getAllAgentsAdmin();
      setAgents(data);
    } catch (error) {
      console.error('Failed to load agents:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function startEdit(agent: CoachingAgentAdmin) {
    setEditingId(agent.id);
    setEditForm(agent);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  async function saveEdit() {
    if (!editingId || !editForm) return;

    try {
      await updateAgent(editingId, editForm);
      await loadAgents();
      cancelEdit();
    } catch (error) {
      console.error('Failed to update agent:', error);
      alert('Failed to update agent');
    }
  }

  async function handleDelete(agentId: string) {
    if (!confirm('Are you sure you want to delete this agent?')) return;

    try {
      await deleteAgent(agentId);
      await loadAgents();
    } catch (error) {
      console.error('Failed to delete agent:', error);
      alert('Failed to delete agent. It may have existing chat sessions.');
    }
  }

  async function handleCreate() {
    if (!editForm.name || !editForm.slug || !editForm.systemPrompt) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await createAgent(editForm as any);
      await loadAgents();
      setIsCreating(false);
      setEditForm({});
    } catch (error) {
      console.error('Failed to create agent:', error);
      alert('Failed to create agent');
    }
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading agents...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-100">Coaching Agents</h2>
        <Button onClick={() => setIsCreating(true)} disabled={isCreating}>
          <Plus className="h-4 w-4 mr-2" />
          Add Agent
        </Button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold mb-4 text-slate-100">Create New Agent</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
                placeholder="Faith Coach"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Slug * (URL-safe, lowercase)
              </label>
              <input
                type="text"
                value={editForm.slug || ''}
                onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase() })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
                placeholder="faith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Icon
              </label>
              <select
                value={editForm.iconName || 'bot'}
                onChange={(e) => setEditForm({ ...editForm, iconName: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
              >
                {HABIT_ICON_OPTIONS.map((icon) => (
                  <option key={icon} value={icon}>
                    {icon}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
                placeholder="Spiritual guidance and values alignment"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                System Prompt *
              </label>
              <textarea
                value={editForm.systemPrompt || ''}
                onChange={(e) => setEditForm({ ...editForm, systemPrompt: e.target.value })}
                rows={8}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 font-mono text-sm"
                placeholder="You are a compassionate coach..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate}>Create Agent</Button>
              <Button
                onClick={() => {
                  setIsCreating(false);
                  setEditForm({});
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Agents List */}
      <div className="space-y-4">
        {agents.map((agent) => {
          const isEditing = editingId === agent.id;

          return (
            <div
              key={agent.id}
              className="bg-slate-800 rounded-lg p-6 border border-slate-700"
            >
              {isEditing ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        Icon
                      </label>
                      <select
                        value={editForm.iconName || agent.iconName}
                        onChange={(e) => setEditForm({ ...editForm, iconName: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
                      >
                        {HABIT_ICON_OPTIONS.map((icon) => (
                          <option key={icon} value={icon}>
                            {icon}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      System Prompt
                    </label>
                    <textarea
                      value={editForm.systemPrompt || ''}
                      onChange={(e) => setEditForm({ ...editForm, systemPrompt: e.target.value })}
                      rows={10}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-100 font-mono text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.isActive ?? agent.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                      id={`active-${agent.id}`}
                    />
                    <label htmlFor={`active-${agent.id}`} className="text-sm text-slate-300">
                      Active
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={saveEdit} size="sm">
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Button onClick={cancelEdit} variant="secondary" size="sm">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-slate-700 rounded-lg">
                        <IconByName name={agent.iconName} className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-100">{agent.name}</h3>
                        <p className="text-sm text-slate-400">{agent.slug}</p>
                        {agent.description && (
                          <p className="text-sm text-slate-300 mt-1">{agent.description}</p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <span
                            className={`
                              text-xs px-2 py-1 rounded
                              ${agent.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}
                            `}
                          >
                            {agent.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {agent.category && (
                            <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300">
                              {agent.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => startEdit(agent)} variant="secondary" size="sm">
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(agent.id)}
                        variant="secondary"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-400 mb-1">System Prompt:</div>
                    <div className="bg-slate-900 rounded p-3 text-sm text-slate-300 font-mono max-h-32 overflow-y-auto">
                      {agent.systemPrompt}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
